import {Branch} from "@/types/models";
import {createContext, PropsWithChildren, useContext, useState} from "react";
import {getPermissions} from "@/services/UserService";
import {getBranch} from "@/services/BranchService";

type PermissionsContextProps = {
    permissions: string[]
    fetchPermissions: (pattern?: string) => Promise<string[]>
    userCan: (action: string) => boolean
    branches: Record<number, Branch>
    fetchBranch: (id: number) => Promise<Record<number, Branch> | undefined>
    findBranch: (idOrHandle: number | string) => Branch | undefined
}

const PermissionsContext = createContext<PermissionsContextProps | undefined>(undefined)

export const PermissionsContextProvider = ({children}: PropsWithChildren) => {
    const [permissions, setPermissions] = useState<string[]>([])
    const [branches, setBranches] = useState<Record<number, Branch>>({})

    const fetchPermissions = async (pattern?: string) => {
        const promise: Promise<string[]> = getPermissions(pattern)
        promise.then((fetchedPermissions: string[]) => {
            setPermissions(Array.from(new Set([...permissions, ...fetchedPermissions])))
        })

        return promise
    }

    const fetchBranch = async (id: number) => {
        const promise: Promise<Record<number, Branch> | undefined> = getBranch(id)
        promise.then((fetchedBranch: Record<number, Branch> | undefined) => {
            if (fetchedBranch) {
                setBranches((current) => ({...current, ...fetchedBranch}))
            }
        })

        return promise
    }

    const findBranch = (idOrHandle: number | string, parent?: Branch): Branch | undefined => {
        const search = parent ? parent.children : branches
        if (!search) return undefined

        let branch = typeof idOrHandle === 'number'
            ? search[idOrHandle]
            : Object.values(search).find(b => b.handle === idOrHandle)

        if (!branch) {
            for (const id in search) {
                branch = findBranch(idOrHandle, search[id])
                if (branch) break;
            }
        }

        return branch
    }

    /**
     * Determines if a user has permission to perform a specific action.
     *
     * The function checks whether the given action matches any entry in the permissions list.
     * Permissions may include wildcards (*) at any level of the action hierarchy.
     *
     * @param {string} action - The action to check against the user's permissions. The action
     *                           is represented as a dot-separated string (e.g., "module.submodule.action").
     * @returns {boolean} - Returns `true` if the user has the necessary permission, otherwise `false`.
     */
    const userCan = (action: string) => {
        const actionParts = action.split(".")
        return permissions.some(p => {
            if (p === action) return true

            const parts = p.split(".")

            for (let i = 0; i < actionParts.length; i++) {
                if (parts[i] !== '*' && parts[i] !== actionParts[i]) return false
            }

            return true
        })
    }

    return <PermissionsContext.Provider value={{
        permissions,
        fetchPermissions,
        userCan,
        branches,
        fetchBranch,
        findBranch,
    }}>
        {children}
    </PermissionsContext.Provider>
}

export function usePermissionsContext(): PermissionsContextProps {
    const context = useContext(PermissionsContext);
    if (!context) throw new Error("No PermissionsContext is defined")
    return context
}
